import React, { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  Chip,
  Button,
  MenuItem,
  Menu,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts'
import {
  LocationOn,
  People,
  Home,
  TrendingUp,
  Download,
  Share,
  Info,
  MoreVert,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ViewList,
  Visibility,
  CompareArrows,
} from '@mui/icons-material'

// Modern color palette with Material UI theme integration
const CHART_COLORS = [
  '#2196F3', // blue
  '#4CAF50', // green
  '#FFC107', // amber
  '#F44336', // red
  '#9C27B0', // purple
  '#00BCD4', // cyan
  '#FF9800', // orange
]

const backendURL = process.env.REACT_APP_BACKEND_URL

// Styled components
const DashboardHeader = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  marginBottom: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '30%',
    height: '100%',
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.dark}33)`,
    zIndex: 1,
  },
}))

const CategoryCard = styled(Card)(({ theme, active }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  borderLeft: active ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
  backgroundColor: active ? theme.palette.action.selected : theme.palette.background.paper,
  '&:hover': {
    backgroundColor: active ? theme.palette.action.selected : theme.palette.action.hover,
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[3],
  },
}))

const StatsCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}))

const ChartContainer = styled(Box)(({ theme }) => ({
  height: 400,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
}))

const LoadingContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '70vh',
})

/**
 * Enhanced Analytics Dashboard Component for CBS Data
 */
const AnalyticsDashboard = ({ address, city, postcode }) => {
  const theme = useTheme()
  const [analyticsData, setAnalyticsData] = useState([])
  const [groupedData, setGroupedData] = useState({})
  const [selectedGroup, setSelectedGroup] = useState('')
  const [loading, setLoading] = useState(true)
  const [userToken, setUserToken] = useState(null)
  const [neighborhood, setNeighborhood] = useState({})
  const [chartType, setChartType] = useState('pie')
  const [vizOptions, setVizOptions] = useState({})
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [activeTab, setActiveTab] = useState(0)

  // Define which groups to display. Modify this array to hide or show specific groups.
  const VISIBLE_GROUPS = [
    'Bevolking',
    'Geslacht',
    'Leeftijdsgroepen',
    'Burgerlijke staat',
    'Herkomstland',
    'Geboren in Nederland',
    'Geboren buiten Nederland',
    'Particuliere huishoudens',
    'Wonen en vastgoed',
    // 'Oppervlakte',
    // 'Stedelijkheid',
    // "Personenauto's",
    // 'Motorvoertuigen',
  ]

  // Load user token
  useEffect(() => {
    const fetchToken = async () => {
      const { data: tokenData, error: tokenError } = await supabase.auth.getSession()
      if (tokenError) console.error('Error fetching token:', tokenError)
      setUserToken(tokenData?.session?.access_token || null)
    }
    fetchToken()
  }, [])

  // Fetch analytics data
  useEffect(() => {
    if (!userToken) return

    setLoading(true)

    fetch(`${backendURL}/analytics/cbs?address=${encodeURIComponent(address)}`, {
      headers: {
        Authorization: `Bearer ${userToken}`,
        Accept: 'application/json',
      },
    })
      .then((res) => res.json())
      .then((response) => {
        const neighborhood = response.neighborhood
        const data = response.analytics

        // Preprocess data for visualization: group items by group_title
        const groups = data.reduce((acc, item) => {
          const group = item.group_title
          if (!acc[group]) {
            acc[group] = []
          }
          acc[group].push(item)
          return acc
        }, {})

        // Filter groups based on VISIBLE_GROUPS so only desired groups are populated
        const filteredGroups = Object.keys(groups).reduce((acc, group) => {
          if (VISIBLE_GROUPS.includes(group)) {
            acc[group] = groups[group]
          }
          return acc
        }, {})

        // Create visualization options for each group in the filtered list
        const vizOptionsByGroup = Object.keys(filteredGroups).reduce((acc, group) => {
          acc[group] = determineOptimalVisualizationType(group, filteredGroups[group])
          return acc
        }, {})

        setGroupedData(filteredGroups)
        setVizOptions(vizOptionsByGroup)
        setNeighborhood(neighborhood)

        const groupTitles = Object.keys(filteredGroups)
        if (groupTitles.length > 0) {
          setSelectedGroup(groupTitles[0])
        }

        setLoading(false)
      })
      .catch((err) => {
        console.error('Error fetching analytics:', err)
        setLoading(false)
      })
  }, [userToken])

  // Determine the best visualization type based on data characteristics
  const determineOptimalVisualizationType = (groupTitle, items) => {
    const totalItems = items.length

    // Detect if it's categorical data (good for pie charts)
    const isCategorical =
      totalItems <= 8 && items.every((item) => typeof item.value === 'number' && item.value >= 0)

    // Detect if it's time series or ordered data (good for bar or line charts)
    const hasOrderedLabels = items.some(
      (item) =>
        item.measure_title.includes('jaar') ||
        /^\d{4}$/.test(item.measure_title) ||
        ['leeftijd', 'age', 'jaar', 'period'].some((term) =>
          item.measure_title.toLowerCase().includes(term)
        )
    )

    if (groupTitle === 'Leeftijdsgroepen' || groupTitle === 'Geslacht') {
      return {
        recommended: 'pie',
        alternates: ['bar', 'table'],
      }
    } else if (hasOrderedLabels) {
      return {
        recommended: 'bar',
        alternates: ['line', 'table'],
      }
    } else if (isCategorical) {
      return {
        recommended: 'pie',
        alternates: ['bar', 'table'],
      }
    } else {
      return {
        recommended: 'table',
        alternates: ['bar'],
      }
    }
  }

  // Handle category selection
  const handleGroupChange = (groupTitle) => {
    setSelectedGroup(groupTitle)
    // Set the default chart type based on recommended visualization
    const recommendedViz = vizOptions[groupTitle]?.recommended || 'pie'
    setChartType(recommendedViz)
  }

  // Handle visualization type change
  const handleChartTypeChange = (type) => {
    setChartType(type)
    setMenuAnchorEl(null)
  }

  // Handle menu open
  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget)
  }

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null)
  }

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  // Prepare data for charts
  const prepareChartData = (items) => {
    // Basic mapping for most chart types
    return items.map((item) => ({
      name: item.measure_title,
      value: item.value !== null ? item.value : 0,
      stringValue: item.string_value || '',
    }))
  }

  // Process special cases for specific group types
  const processSpecialGroupData = (groupTitle, items) => {
    switch (groupTitle) {
      case 'Particuliere huishoudens': {
        // Extract data for households with and without children
        const withChildren = items.find((item) =>
          item.measure_title.toLowerCase().includes('met kinderen')
        )
        const withoutChildren = items.find((item) =>
          item.measure_title.toLowerCase().includes('zonder kinderen')
        )

        return [
          { name: 'Met kinderen', value: withChildren ? withChildren.value : 0 },
          { name: 'Zonder kinderen', value: withoutChildren ? withoutChildren.value : 0 },
        ]
      }
      default:
        return prepareChartData(items)
    }
  }

  // Render appropriate chart based on selected type
  const renderVisualization = (groupTitle, items) => {
    if (!items || items.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No data available for this category
          </Typography>
        </Box>
      )
    }

    const chartData = processSpecialGroupData(groupTitle, items)

    // Render based on chart type
    switch (chartType) {
      case 'pie':
        return renderPieChart(chartData)
      case 'bar':
        return renderBarChart(chartData)
      case 'line':
        return renderBarChart(chartData, true) // Reuse bar chart with line mode
      case 'table':
      default:
        return renderDataTable(items)
    }
  }

  // Render pie chart
  const renderPieChart = (data) => {
    return (
      <ChartContainer>
        <Typography variant="h6" gutterBottom>
          {selectedGroup} Distribution
        </Typography>
        <Box sx={{ flex: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip
                formatter={(value) => [
                  `${value.toLocaleString()} (${(
                    (value / data.reduce((sum, entry) => sum + entry.value, 0)) *
                    100
                  ).toFixed(1)}%)`,
                  'Value',
                ]}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </ChartContainer>
    )
  }

  // Render bar chart (or line chart with isLine=true)
  const renderBarChart = (data, isLine = false) => {
    // Sort data for better visualization (if applicable)
    const sortedData = [...data]

    return (
      <ChartContainer>
        <Typography variant="h6" gutterBottom>
          {selectedGroup} {isLine ? 'Trend' : 'Comparison'}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sortedData} margin={{ top: 20, right: 30, left: 20, bottom: 70 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={70} interval={0} />
              <YAxis />
              <RechartsTooltip formatter={(value) => value.toLocaleString()} />
              <Legend />
              {isLine ? (
                <Bar dataKey="value" fill={theme.palette.primary.main} shape={<CustomizedDot />} />
              ) : (
                <Bar dataKey="value">
                  {sortedData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </ChartContainer>
    )
  }

  // Custom dot component for line charts
  const CustomizedDot = (props) => {
    const { cx, cy, index } = props
    return (
      <circle
        cx={cx}
        cy={cy}
        r={5}
        fill={CHART_COLORS[index % CHART_COLORS.length]}
        stroke="none"
      />
    )
  }

  // Render data table for detailed view
  const renderDataTable = (items) => {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {selectedGroup} Details
        </Typography>
        <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
          <Grid container spacing={2}>
            {items.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <StatsCard variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {item.measure_title}
                    </Typography>
                    <Typography variant="h5" component="div">
                      {item.value !== null
                        ? typeof item.value === 'number'
                          ? item.value.toLocaleString()
                          : item.value
                        : item.string_value || 'N/A'}
                    </Typography>
                  </CardContent>
                </StatsCard>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    )
  }

  // Overview tab content
  const renderOverviewTab = () => {
    // Extract key metrics for the overview

    // Population from 'Bevolking' group with measure 'Aantal inwoners'
    const population =
      groupedData['Bevolking']?.find((item) =>
        item.measure_title.toLowerCase().includes('aantal inwoners')
      )?.value || 'N/A'

    // Housing count from 'Wonen en vastgoed' group with measure 'Woningvoorraad'
    const housingCount =
      groupedData['Wonen en vastgoed']?.find((item) =>
        item.measure_title.toLowerCase().includes('woningvoorraad')
      )?.value || 'N/A'

    // Avg. Income is rendered only if the 'Inkomen' group exists
    const meanIncomeItem = groupedData['Inkomen']?.find((item) =>
      item.measure_title.toLowerCase().includes('gemiddeld')
    )
    const meanIncome = meanIncomeItem ? meanIncomeItem.value : null

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Neighborhood Overview
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <StatsCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <People sx={{ color: theme.palette.primary.main, mr: 1 }} />
                  <Typography variant="h6" component="div">
                    Population
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ mb: 1 }}>
                  {typeof population === 'number' ? population.toLocaleString() : population}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total residents in the neighborhood
                </Typography>
              </CardContent>
            </StatsCard>
          </Grid>

          {housingCount !== 'N/A' && (
            <Grid item xs={12} sm={6} md={4}>
              <StatsCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Home sx={{ color: theme.palette.primary.main, mr: 1 }} />
                    <Typography variant="h6" component="div">
                      Housing
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" sx={{ mb: 1 }}>
                    {typeof housingCount === 'number'
                      ? housingCount.toLocaleString()
                      : housingCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total housing units
                  </Typography>
                </CardContent>
              </StatsCard>
            </Grid>
          )}

          {meanIncome !== null && (
            <Grid item xs={12} sm={6} md={4}>
              <StatsCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUp sx={{ color: theme.palette.primary.main, mr: 1 }} />
                    <Typography variant="h6" component="div">
                      Avg. Income
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" sx={{ mb: 1 }}>
                    {typeof meanIncome === 'number'
                      ? `€${meanIncome.toLocaleString()}`
                      : meanIncome}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mean household income per year
                  </Typography>
                </CardContent>
              </StatsCard>
            </Grid>
          )}
        </Grid>

        {/* Highlight a demographic insight */}
        {groupedData['Leeftijdsgroepen'] && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Demographic Highlights
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={prepareChartData(groupedData['Leeftijdsgroepen'])}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {prepareChartData(groupedData['Leeftijdsgroepen']).map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Box>
        )}

        {/* Quick insights section */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Neighborhood Insights
          </Typography>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body1">
                  {neighborhood.name} is located in {neighborhood.city}, {neighborhood.province}.
                  This neighborhood has a population of{' '}
                  {typeof population === 'number' ? population.toLocaleString() : population}{' '}
                  residents across{' '}
                  {typeof housingCount === 'number' ? housingCount.toLocaleString() : housingCount}{' '}
                  housing units.
                </Typography>
              </Grid>

              {groupedData['Bevolking'] && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      icon={<People />}
                      label={`Density: ${
                        groupedData['Bevolking'].find((item) =>
                          item.measure_title.toLowerCase().includes('bevolkingsdichtheid')
                        )?.value || 'N/A'
                      } per km²`}
                      color="primary"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                  </Box>
                </Grid>
              )}

              {groupedData['Oppervlakte'] && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      icon={<CompareArrows />}
                      label={`Area: ${
                        groupedData['Oppervlakte'].find((item) =>
                          item.measure_title.toLowerCase().includes('totaal')
                        )?.value || 'N/A'
                      } ha`}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Box>
      </Box>
    )
  }

  // Explore tab content (the main data exploration interface)
  const renderExploreTab = () => {
    const groupTitles = Object.keys(groupedData)

    return (
      <Box sx={{ mt: 3 }}>
        <Grid container spacing={2}>
          {/* Sidebar Categories */}
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
                Data Categories
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {groupTitles.map((group) => (
                  <CategoryCard
                    key={group}
                    active={selectedGroup === group}
                    onClick={() => handleGroupChange(group)}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight={selectedGroup === group ? 'medium' : 'normal'}
                      >
                        {group}
                      </Typography>
                    </CardContent>
                  </CategoryCard>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">{selectedGroup}</Typography>
                <Box>
                  <Tooltip title="Visualization Options">
                    <IconButton onClick={handleMenuClick} size="small">
                      <MoreVert />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem
                      onClick={() => handleChartTypeChange('pie')}
                      disabled={chartType === 'pie'}
                    >
                      <PieChartIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                      Pie Chart
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleChartTypeChange('bar')}
                      disabled={chartType === 'bar'}
                    >
                      <BarChartIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                      Bar Chart
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleChartTypeChange('table')}
                      disabled={chartType === 'table'}
                    >
                      <ViewList sx={{ mr: 1, fontSize: '1.25rem' }} />
                      Data Table
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleMenuClose}>
                      <Download sx={{ mr: 1, fontSize: '1.25rem' }} />
                      Download Data
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>

              {groupedData[selectedGroup] &&
                renderVisualization(selectedGroup, groupedData[selectedGroup])}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    )
  }

  // Compare tab content (for future implementation)
  const renderCompareTab = () => {
    return (
      <Box sx={{ mt: 3, p: 3, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom color="text.secondary">
          Neighborhood Comparison
        </Typography>
        <Typography variant="body1" paragraph color="text.secondary">
          This feature will allow comparing {neighborhood.name} with other neighborhoods.
        </Typography>
        <Button variant="outlined" startIcon={<Visibility />}>
          Coming Soon
        </Button>
      </Box>
    )
  }

  // Render loading state
  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading neighborhood data...
        </Typography>
      </LoadingContainer>
    )
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {/* Dashboard Header */}
      <DashboardHeader elevation={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <LocationOn sx={{ fontSize: 40, opacity: 0.9 }} />
          </Grid>
          <Grid item xs>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
              {neighborhood.name}
            </Typography>
            <Typography variant="subtitle1">
              {neighborhood.city}, {neighborhood.province}
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <Chip
                size="small"
                label={`CBS Code: ${neighborhood.code || 'Unknown'}`}
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'inherit' }}
              />
              <Chip
                size="small"
                label={`Last Updated: 2025`}
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'inherit' }}
              />
            </Box>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Share this analysis">
                <IconButton color="inherit">
                  <Share />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download report">
                <IconButton color="inherit">
                  <Download />
                </IconButton>
              </Tooltip>
              <Tooltip title="About this data">
                <IconButton color="inherit">
                  <Info />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </DashboardHeader>

      {/* Dashboard Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab icon={<Home />} label="Overview" />
          <Tab icon={<PieChartIcon />} label="Explore Data" />
          <Tab icon={<CompareArrows />} label="Compare" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && renderOverviewTab()}
      {activeTab === 1 && renderExploreTab()}
      {activeTab === 2 && renderCompareTab()}
    </Box>
  )
}

export default AnalyticsDashboard
